// The Schowek panel for the house, the front yard and Podwórko — the farm's look (farm/FarmStorage.js):
// everything bought but not placed here, with counts; a tap closes it and starts placing that item.
import { GAME_WIDTH, GAME_HEIGHT } from '../config.js';
import { lockInput, unlockInput } from './inputLock.js';
import { ensureLoaded } from './sceneLoading.js';
import { storagePage } from '../farm/farmStorage.mjs';
import { gamePanel, gameButton, gameCard, woodTitle, pageArrows } from './gameUi.js';
import { iconText } from './iconText.js';
import { ANIMAL_EMOJI, BUDA_ACC } from './animalCopy.js';
import { pinToCamera } from './pin.mjs';

const FONT = 'system-ui, -apple-system, sans-serif';
const COLS = 4, CARD_W = 200, CARD_H = 170, GAP = 14;
const texOf = (it) => `${it.type}_${it.item}`;

export async function openHouseStorage(scene, items, { onPick } = {}) {
  lockInput(scene);
  await ensureLoaded(scene, [...new Set(items.map(texOf)), 'plot_empty']).catch(() => {});
  const layer = scene.add.container(0, 0).setDepth(2000).setScrollFactor(0);
  const close = (pick = null) => { unlockInput(scene); layer.destroy(true); if (pick) onPick?.(pick); };
  const overlay = scene.add.rectangle(0, 0, GAME_WIDTH, GAME_HEIGHT, 0x000000, 0.6).setOrigin(0, 0).setInteractive();
  overlay.on('pointerup', () => close());
  const panelHit = scene.add.rectangle(GAME_WIDTH / 2, GAME_HEIGHT / 2, 1040, 560, 0xffffff, 0.001).setInteractive();
  const panel = gamePanel(scene, GAME_WIDTH / 2, GAME_HEIGHT / 2, 1040, 560);
  const title = woodTitle(scene, GAME_WIDTH / 2, GAME_HEIGHT / 2 - 276, '{crate} Schowek', { fontSize: 24 });
  layer.add([overlay, panelHit, panel, title]);
  if (!items.length) layer.add(iconText(scene, GAME_WIDTH / 2, GAME_HEIGHT / 2, 'Schowek jest pusty', { fontFamily: FONT, fontSize: '20px', color: '#555' }).setOrigin(0.5));
  const x0 = GAME_WIDTH / 2 - (COLS * CARD_W + (COLS - 1) * GAP) / 2 + CARD_W / 2;
  let page = 0, body = null;
  const draw = () => {
    body?.destroy(true);
    body = scene.add.container(0, 0);
    layer.add(body);
    const view = storagePage(items, page);
    page = view.page;
    view.items.forEach((it, i) => {
      const cx = x0 + (i % COLS) * (CARD_W + GAP), cy = GAME_HEIGHT / 2 - 110 + Math.floor(i / COLS) * (CARD_H + GAP);
      const card = gameCard(scene, cx, cy, CARD_W, CARD_H).setInteractive({ useHandCursor: true });
      const img = scene.add.image(cx, cy - 25, scene.textures.exists(texOf(it)) ? texOf(it) : 'plot_empty');
      const src = img.texture.getSourceImage();
      const k = Math.min(110 / src.width, 85 / src.height); img.setDisplaySize(src.width * k, src.height * k);
      const name = iconText(scene, cx, cy + 40, it.name, { fontFamily: FONT, fontSize: '16px', color: '#000', fontStyle: 'bold', align: 'center', wordWrap: { width: CARD_W - 16 } }).setOrigin(0.5);
      const count = iconText(scene, cx + CARD_W / 2 - 12, cy - CARD_H / 2 + 10, `× ${it.count}`, { fontFamily: FONT, fontSize: '16px', color: '#b8860b', fontStyle: 'bold' }).setOrigin(1, 0);
      card.on('pointerup', () => close(it));
      body.add([card, img, name, count]);
      if (it.waiting > 0) {   // an animal bought before its house moves in the moment this is placed
        body.add(iconText(scene, cx, cy + 64, `${ANIMAL_EMOJI[it.forAnimal] ?? '{paw}'} ×${it.waiting} czeka, postaw ${BUDA_ACC[it.item] ?? it.item}!`, {
          fontFamily: FONT, fontSize: '12px', color: '#fff', fontStyle: 'bold', backgroundColor: '#d9534f', padding: { x: 5, y: 2 },
        }).setOrigin(0.5));
      }
    });
    if (view.pages > 1) {
      const arrowDx = (COLS * CARD_W + (COLS - 1) * GAP) / 2 + 52;
      body.add(pageArrows(scene, GAME_WIDTH / 2, GAME_HEIGHT / 2 - 18, arrowDx, page, view.pages, (p) => { page = p; draw(); }));
      body.add(iconText(scene, GAME_WIDTH / 2, GAME_HEIGHT / 2 + 200, `${page + 1} / ${view.pages}`, { fontFamily: FONT, fontSize: '15px', color: '#555' }).setOrigin(0.5));
    }
    pinToCamera(layer);   // the layer's own setScrollFactor(0) leaves its children hit-testing in world space (pin.mjs)
  };
  draw();
  layer.add(gameButton(scene, GAME_WIDTH / 2, GAME_HEIGHT / 2 + 235, 200, 46, 'Zamknij', () => close(), { variant: 'wood', fontSize: 18 }));
  pinToCamera(layer);
}
